import React, { useCallback, useEffect, useMemo, useState, FormEvent } from 'react';
import { CompanyUserDto, tenancyApi } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePaginatedList } from '../../hooks/usePagination';
import { Link, useLocation } from '../../router/Router';
import { Badge, formatDateTime } from '../../components/ui/Badge';
import { Dialog } from '../../components/ui/Dialog';
import { IconPlus, IconUsers } from '../../components/ui/Icons';
import { PaginationBar } from '../../components/ui/PaginationBar';

export function SettingsPage() {
  const { user, company, refreshUserSession } = useAuth();
  const { pushToast } = useToast();
  const location = useLocation();

  const activeTab: 'user' | 'company' = location.pathname.includes('/settings/company')
    ? 'company'
    : 'user';

  // User Profile & Preferences State
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [fullName, setFullName] = useState<string>('');
  const [isLoginAsAllowed, setIsLoginAsAllowed] = useState<boolean>(true);
  const [disableTaskNotification, setDisableTaskNotification] = useState<boolean>(false);
  const [savingPrefs, setSavingPrefs] = useState<boolean>(false);

  // Company Team Members State
  const [companyUsers, setCompanyUsers] = useState<CompanyUserDto[]>([]);
  const [loadingTeam, setLoadingTeam] = useState<boolean>(false);
  const [memberSearch, setMemberSearch] = useState<string>('');
  const [inviteOpen, setInviteOpen] = useState<boolean>(false);
  const [newUserEmail, setNewUserEmail] = useState<string>('');
  const [newUserFullName, setNewUserFullName] = useState<string>('');
  const [newUserProcoreId, setNewUserProcoreId] = useState<string>('');
  const [creatingUser, setCreatingUser] = useState<boolean>(false);

  const filteredMembers = useMemo(() => {
    const q = memberSearch.trim().toLowerCase();
    if (!q) return companyUsers;
    return companyUsers.filter(({ user: member, has_billing_permission }) => {
      const roleLabel = has_billing_permission ? 'billing contact' : 'standard member';
      const explicitRole = (member as { role?: string }).role || '';
      return (
        member.full_name.toLowerCase().includes(q) ||
        (member.first_name && member.first_name.toLowerCase().includes(q)) ||
        (member.last_name && member.last_name.toLowerCase().includes(q)) ||
        member.email.toLowerCase().includes(q) ||
        roleLabel.includes(q) ||
        explicitRole.toLowerCase().includes(q)
      );
    });
  }, [companyUsers, memberSearch]);

  const membersPagination = usePaginatedList(filteredMembers, 10);

  useEffect(() => {
    if (user) {
      setFirstName(user.first_name || '');
      setLastName(user.last_name || '');
      setFullName(user.full_name || '');
      setIsLoginAsAllowed(user.is_login_as_allowed);
      setDisableTaskNotification(user.disable_task_notification);
    }
  }, [user]);

  const loadCompanyTeam = useCallback(async () => {
    setLoadingTeam(true);
    try {
      const members = await tenancyApi.getCompanyUsers(company?.id);
      setCompanyUsers(Array.isArray(members) ? members : []);
    } catch {
      setCompanyUsers([]);
    } finally {
      setLoadingTeam(false);
    }
  }, [company?.id]);

  useEffect(() => {
    if (activeTab === 'company') {
      void loadCompanyTeam();
    }
  }, [activeTab, loadCompanyTeam]);

  const handleSaveUserPreferences = async (e: FormEvent) => {
    e.preventDefault();
    setSavingPrefs(true);
    try {
      await tenancyApi.updateMyPreferences({
        first_name: firstName.trim() || undefined,
        last_name: lastName.trim() || undefined,
        full_name: fullName.trim() || undefined,
        is_login_as_allowed: isLoginAsAllowed,
        disable_task_notification: disableTaskNotification,
      });
      await refreshUserSession();
      pushToast({
        tone: 'success',
        title: 'User preferences saved',
        description: 'Your personal profile and notification settings have been updated.',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Failed to save preferences',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setSavingPrefs(false);
    }
  };

  const handleCreateTeamMember = async (e: FormEvent) => {
    e.preventDefault();
    if (!newUserEmail.trim() || !newUserFullName.trim()) return;
    setCreatingUser(true);
    try {
      await tenancyApi.createUser({
        company_id: company?.id,
        email: newUserEmail.trim(),
        full_name: newUserFullName.trim(),
        procore_user_id: newUserProcoreId ? Number(newUserProcoreId) : undefined,
      });
      setInviteOpen(false);
      setNewUserEmail('');
      setNewUserFullName('');
      setNewUserProcoreId('');
      await loadCompanyTeam();
      pushToast({
        tone: 'success',
        title: 'Team member added',
      });
    } catch (err) {
      pushToast({
        tone: 'error',
        title: 'Could not add team member',
        description: err instanceof Error ? err.message : 'Request failed',
      });
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900">
            Account &amp; Company Settings
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Manage personal notification &amp; support access preferences or view your company team
            directory.
          </p>
        </div>

        <div className="inline-flex rounded-xl border border-slate-200 bg-white p-1">
          <Link
            to="/settings/user"
            className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
              activeTab === 'user'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            User Profile &amp; Preferences
          </Link>
          <Link
            to="/settings/company"
            className={`rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors ${
              activeTab === 'company'
                ? 'bg-indigo-600 text-white'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            Company &amp; Team Members
          </Link>
        </div>
      </div>

      {activeTab === 'user' ? (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs max-w-2xl space-y-6">
          <div>
            <h2 className="text-base font-bold text-slate-900">Personal Profile &amp; Security</h2>
            <p className="text-xs text-slate-500">
              Update your display name, email notification preferences, and support impersonation
              consent.
            </p>
          </div>

          <form onSubmit={handleSaveUserPreferences} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Last Name</label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Full Display Name
              </label>
              <input
                type="text"
                required
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isLoginAsAllowed}
                  onChange={(e) => setIsLoginAsAllowed(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <div>
                  <span className="block text-xs font-semibold text-slate-900">
                    Allow Platform Support &ldquo;Login As&rdquo; Impersonation
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    When enabled, authorized Sync administrators can temporarily inspect your
                    workspace to troubleshoot pipe mappings.
                  </span>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={disableTaskNotification}
                  onChange={(e) => setDisableTaskNotification(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300"
                />
                <div>
                  <span className="block text-xs font-semibold text-slate-900">
                    Disable Task Sync Email Notifications
                  </span>
                  <span className="block text-xs text-slate-500 mt-0.5">
                    Suppress automated task change and schedule digest emails for your user account.
                  </span>
                </div>
              </label>
            </div>

            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={savingPrefs}
                className="rounded-xl bg-indigo-600 px-5 py-2.5 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {savingPrefs ? 'Saving Preferences…' : 'Save Preferences'}
              </button>
            </div>
          </form>
        </section>
      ) : (
        <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-2xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2">
                <IconUsers className="w-5 h-5 text-indigo-600" />
                <h2 id="team-directory-heading" className="text-base font-bold text-slate-900">
                  {company?.name || 'Company'} Team Directory
                </h2>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Users associated with this Procore company tenant.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2.5">
              <input
                type="search"
                aria-label="Search team directory"
                value={memberSearch}
                onChange={(e) => {
                  setMemberSearch(e.target.value);
                  membersPagination.setPage(0);
                }}
                placeholder="Search name, email, or role…"
                className="w-full sm:w-60 rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none"
              />

              <button
                type="button"
                onClick={() => setInviteOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                <IconPlus className="w-4 h-4" />
                <span>Add Team Member</span>
              </button>
            </div>
          </div>

          <div className="overflow-hidden rounded-xl border border-slate-200">
            <div
              tabIndex={0}
              role="region"
              aria-labelledby="team-directory-heading"
              className="table-scroll-container"
            >
              <table className="min-w-full divide-y divide-slate-200 text-left text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase tracking-wider font-semibold">
                  <tr>
                    <th className="px-4 py-3">Member</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">OAuth Connections</th>
                    <th className="px-4 py-3">Billing Role</th>
                    <th className="px-4 py-3">Support Login-As</th>
                    <th className="px-4 py-3">Last Login</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200 bg-white">
                  {loadingTeam ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        Loading company team members…
                      </td>
                    </tr>
                  ) : filteredMembers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                        {companyUsers.length === 0
                          ? 'No team members returned. Click “Add Team Member” to invite a colleague.'
                          : 'No team members match your search.'}
                      </td>
                    </tr>
                  ) : (
                    membersPagination.pageItems.map(({ user: member, has_billing_permission }) => (
                      <tr key={member.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="font-semibold text-slate-900">{member.full_name}</div>
                          <div className="text-slate-500">{member.email}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={member.status === 'active' ? 'success' : 'neutral'} dot>
                            {member.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {member.procore_oauth?.connected && (
                              <Badge variant="purple">Procore</Badge>
                            )}
                            {member.smartsheet_oauth?.connected && (
                              <Badge variant="info">Smartsheet</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {has_billing_permission ? (
                            <Badge variant="purple">Billing Contact</Badge>
                          ) : (
                            <span className="text-slate-400">Standard Member</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {member.is_login_as_allowed ? (
                            <Badge variant="success">Allowed</Badge>
                          ) : (
                            <Badge variant="neutral">Restricted</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-slate-600 tabular-nums">
                          {formatDateTime(member.last_login_at)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <PaginationBar pagination={membersPagination} itemLabel="team members" />
          </div>
        </section>
      )}

      {/* Add Team Member Modal */}
      <Dialog
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Add Company Team Member"
        subtitle="Create a user record in this company."
      >
        <form onSubmit={handleCreateTeamMember} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Full Name <span className="text-rose-600">*</span>
            </label>
            <input
              type="text"
              required
              value={newUserFullName}
              onChange={(e) => setNewUserFullName(e.target.value)}
              placeholder="Jordan Rivera"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Email Address <span className="text-rose-600">*</span>
            </label>
            <input
              type="email"
              required
              value={newUserEmail}
              onChange={(e) => setNewUserEmail(e.target.value)}
              placeholder="jordan@construction.com"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-slate-700 mb-1">
              Procore User ID (optional)
            </label>
            <input
              type="number"
              value={newUserProcoreId}
              onChange={(e) => setNewUserProcoreId(e.target.value)}
              placeholder="891234"
              className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setInviteOpen(false)}
              className="rounded-lg border border-slate-300 px-4 py-2 text-xs font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creatingUser}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {creatingUser ? 'Adding…' : 'Add Member'}
            </button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
